import API_CONFIG from '../config/api';
import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { useAuth } from '../hooks/useAuth';
import PhotoPopup from './PhotoPopup';

const accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';
mapboxgl.accessToken = accessToken;

const MapViewer = ({ selectedDataset, onMapReady }) => {
  const { makeAuthenticatedRequest } = useAuth();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [photoPopup, setPhotoPopup] = useState({ isOpen: false, photo: null });

  // Calculate adaptive zoom level based on window size and browser zoom
  const getAdaptiveZoom = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const devicePixelRatio = window.devicePixelRatio || 1;
    
    // Base zoom calculation considering viewport size
    let baseZoom;
    
    // For very small screens (mobile)
    if (width < 768) {
      baseZoom = 0.5;
    }
    // For tablet sizes
    else if (width < 1024) {
      baseZoom = 1;
    }
    // For desktop
    else if (width < 1440) {
      baseZoom = 1.5;
    }
    // For large screens
    else {
      baseZoom = 2;
    }
    
    // Adjust for browser zoom (devicePixelRatio)
    // Higher devicePixelRatio (zoomed in) = reduce zoom level
    // Lower devicePixelRatio (zoomed out) = increase zoom level
    const zoomAdjustment = Math.log2(devicePixelRatio) * 0.5;
    
    // Consider viewport height as well
    const heightFactor = height < 600 ? -0.3 : (height > 900 ? 0.2 : 0);
    
    const finalZoom = Math.max(0.1, Math.min(4, baseZoom - zoomAdjustment + heightFactor));
    
    return finalZoom;
  };

  useEffect(() => {
    if (map.current || !mapContainer.current) return;
    
    try {
      const adaptiveZoom = getAdaptiveZoom();
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [-85, 25],
        zoom: adaptiveZoom,
      });

      map.current.on('load', () => {
        setMapLoaded(true);
      });

      map.current.on('error', (e) => {
        console.error('Map error:', e);
      });

      // Add navigation controls (zoom only, no rotation)
      map.current.addControl(new mapboxgl.NavigationControl({
        showCompass: false,  // Removes rotation control
        showZoom: true       // Keeps zoom controls
      }), 'top-right');

      // Fullscreen control removed
    } catch (error) {
      console.error('Error creating map:', error);
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
        setMapLoaded(false);
      }
    };
  }, []);

  // Handle window resize: keep canvas sized but do not override user zoom
  useEffect(() => {
    const handleResize = () => {
      if (map.current && mapLoaded) {
        try { map.current.resize(); } catch (_) {}
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mapLoaded]);

  useEffect(() => {
    if (mapLoaded && map.current && onMapReady) {
      onMapReady(map.current);
    }
  }, [mapLoaded, onMapReady]);

  useEffect(() => {
    if (!mapLoaded || !selectedDataset || !map.current) return;

    const loadDataset = async () => {
      try {
        const response = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/data/datasets/${selectedDataset.id}/geojson`);
        const geoJsonData = await response.json();

        const sourceId = 'selected-dataset';
        const layerId = 'selected-dataset-layer';

        // Remove existing layer/source if any
        if (map.current.getLayer(layerId)) {
          map.current.removeLayer(layerId);
        }
        if (map.current.getSource(sourceId)) {
          map.current.removeSource(sourceId);
        }
        
        // Clean up existing photo markers
        if (window.photoMarkers) {
          window.photoMarkers.forEach(marker => marker.remove());
          window.photoMarkers = [];
        }

        // Add new source and layer
        map.current.addSource(sourceId, {
          type: 'geojson',
          data: geoJsonData
        });

        // Style based on geometry type
        const geometryType = selectedDataset.geometry_type?.toLowerCase();
        let layerStyle;

        switch (geometryType) {
          case 'photocollection':
            // Use custom HTML markers for photo collections
            geoJsonData.features.forEach((feature, index) => {
              const coordinates = feature.geometry.coordinates;
              const properties = feature.properties;
              
              // Create thumbnail element
              const thumbnailEl = document.createElement('div');
              thumbnailEl.className = 'photo-marker';
              thumbnailEl.style.cssText = `
                width: 50px;
                height: 50px;
                border-radius: 50%;
                border: 3px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                background-size: cover;
                background-position: center;
                cursor: pointer;
                transition: transform 0.2s ease;
              `;
              
              // Set background image from thumbnail URL
              if (properties.thumbnail_url) {
                thumbnailEl.style.backgroundImage = `url('${API_CONFIG.BASE_URL.replace('/api', '')}${properties.thumbnail_url}')`;
              } else {
                // Fallback to a camera icon
                thumbnailEl.style.backgroundColor = '#ff6b6b';
                thumbnailEl.innerHTML = '📷';
                thumbnailEl.style.display = 'flex';
                thumbnailEl.style.alignItems = 'center';
                thumbnailEl.style.justifyContent = 'center';
                thumbnailEl.style.fontSize = '20px';
              }
              
              // Add hover effect
              thumbnailEl.addEventListener('mouseenter', () => {
                thumbnailEl.style.transform = 'scale(1.1)';
              });
              
              thumbnailEl.addEventListener('mouseleave', () => {
                thumbnailEl.style.transform = 'scale(1.0)';
              });
              
              // Add click handler to open image in new tab
              thumbnailEl.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                if (properties.sharepoint_url) {
                  // Open SharePoint image in new tab
                  window.open(properties.sharepoint_url, '_blank');
                } else {
                  console.warn('No SharePoint URL available for photo:', properties.filename);
                }
              });
              
              // Create and add marker
              const marker = new mapboxgl.Marker(thumbnailEl)
                .setLngLat(coordinates)
                .addTo(map.current);
                
              // Store marker reference for cleanup
              if (!window.photoMarkers) window.photoMarkers = [];
              window.photoMarkers.push(marker);
            });
            
            // Don't add a regular layer for photo collections
            layerStyle = null;
            break;
            
          case 'point':
          case 'multipoint':
            layerStyle = {
              id: layerId,
              source: sourceId,
              type: 'circle',
              paint: {
                'circle-radius': 8,
                'circle-color': '#ff6b6b',
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff'
              }
            };
            break;
          
          case 'linestring':
          case 'multilinestring':
            layerStyle = {
              id: layerId,
              source: sourceId,
              type: 'line',
              paint: {
                'line-color': '#ff6b6b',
                'line-width': 4
              }
            };
            break;
          
          case 'polygon':
          case 'multipolygon':
            map.current.addLayer({
              id: layerId + '-fill',
              source: sourceId,
              type: 'fill',
              paint: {
                'fill-color': '#ff6b6b',
                'fill-opacity': 0.5
              }
            });
            layerStyle = {
              id: layerId,
              source: sourceId,
              type: 'line',
              paint: {
                'line-color': '#ff6b6b',
                'line-width': 3
              }
            };
            break;
          
          default:
            layerStyle = {
              id: layerId,
              source: sourceId,
              type: 'circle',
              paint: {
                'circle-radius': 6,
                'circle-color': '#ff6b6b'
              }
            };
        }

        // Only add layer if layerStyle is defined (not null for photo collections)
        if (layerStyle) {
          map.current.addLayer(layerStyle);
        }

        // Fit map to data bounds
        if (geoJsonData.features.length > 0) {
          const bounds = new mapboxgl.LngLatBounds();
          
          geoJsonData.features.forEach(feature => {
            if (feature.geometry.type === 'Point') {
              bounds.extend(feature.geometry.coordinates);
            } else if (feature.geometry.coordinates) {
              const coords = flattenCoordinates(feature.geometry.coordinates);
              coords.forEach(coord => bounds.extend(coord));
            }
          });
          
          if (!bounds.isEmpty()) {
            map.current.fitBounds(bounds, { padding: 50 });
          }
        }

        // Add popup on click (only for non-photo layers)
        if (layerStyle && geometryType !== 'photocollection') {
          map.current.on('click', layerId, (e) => {
            const feature = e.features[0];
            const properties = feature.properties;
            
            // Show regular mapbox popup
            let popupContent = `<h3>${selectedDataset.name}</h3>`;
            if (properties && Object.keys(properties).length > 0) {
              popupContent += '<div class="popup-properties">';
              Object.entries(properties).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                  popupContent += `<div><strong>${key}:</strong> ${value}</div>`;
                }
              });
              popupContent += '</div>';
            }

            new mapboxgl.Popup()
              .setLngLat(e.lngLat)
              .setHTML(popupContent)
              .addTo(map.current);
          });

          // Change cursor on hover
          map.current.on('mouseenter', layerId, () => {
            map.current.getCanvas().style.cursor = 'pointer';
          });

          map.current.on('mouseleave', layerId, () => {
            map.current.getCanvas().style.cursor = '';
          });
        }

      } catch (error) {
        console.error('Error loading dataset:', error);
      }
    };

    loadDataset();
  }, [selectedDataset, mapLoaded]);

  const flattenCoordinates = (coords) => {
    if (typeof coords[0] === 'number') {
      return [coords];
    }
    return coords.flat(2);
  };

  const handleClosePhotoPopup = () => {
    setPhotoPopup({ isOpen: false, photo: null });
  };

  return (
    <div className="map-viewer">
      <div ref={mapContainer} className="map-container" />
      {!accessToken && (
        <div className="map-error">
          <h3>Map Configuration Error</h3>
          <p>Mapbox access token is missing. Please check your environment configuration.</p>
        </div>
      )}
      {selectedDataset && (
        <div className="map-info">
          <div className="selected-dataset">
            <strong>Viewing:</strong> {selectedDataset.name}
            <span className="feature-count">({selectedDataset.feature_count} features)</span>
          </div>
        </div>
      )}
      
      {/* Photo Popup */}
      <PhotoPopup
        photo={photoPopup.photo}
        isOpen={photoPopup.isOpen}
        onClose={handleClosePhotoPopup}
      />
    </div>
  );
};

export default MapViewer; 
